import { useMemo } from 'react';
import { RankedBars } from '../../charts/RankedBars';
import { Card, ChartCard, DataTable, Stat, StatusChip, type Column } from '../../components/data';
import { ErrorBanner, Loading } from '../../components/ui';
import { formatCompactCurrency, num, pct, relativeDays, snakeLabel, urgency } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { Employee } from '../../lib/types';
import { registrationBand, type RegistrationRow, type RegistrationsData } from './adminTypes';
import { RegisterPanel, formatIsoShort, type RegisterSpec } from './adminUi';

const REGISTRATION_TYPE = ['registration', 'insurance', 'licence', 'certification', 'tax'] as const;
const REGISTRATION_STATUS = ['current', 'renewal_in_progress', 'lapsed', 'not_required'] as const;

/**
 * Business registrations, insurance, licences and certifications.
 *
 * NOT the Compliance module, though the app this came from called this table
 * `compliance_items`. That module is frameworks — SOC 2, ISO 27001 — their
 * requirements and the evidence behind them. This is the corporate paperwork that
 * has to stay in date: the company registration, the tax numbers, the liability
 * policies, the trade licences. Different owners, different consequences of
 * getting it wrong. See the header of migration 048.
 *
 * `RenewalLeadDays` is per item rather than one global threshold, and that is
 * what makes the "needs starting" count trustworthy: a trade licence renews in a
 * week and a liability policy takes two months, so one shared warning window
 * would cry wolf on the first and stay silent on the second until it was too
 * late.
 */
export function AdminRegistrations({
  canWrite,
  onChanged,
}: {
  canWrite: boolean;
  onChanged: () => void;
}) {
  const analytics = useResource<RegistrationsData>('/admin/analytics/registrations');
  const employees = useResource<Employee[]>('/employees?status=active');

  const spec = useMemo<RegisterSpec<RegistrationRow>>(
    () => ({
      resource: 'registrations',
      title: 'Registrations, insurance and licences',
      singular: 'Registration',
      emptyTitle: 'Nothing on the register yet',
      emptyBody:
        'Add the paperwork that has to stay in date: the company registration, tax numbers, liability and cyber policies, trade licences, certifications. The renewal lead time is worth setting per item — it is what decides when this tab starts asking about it.',
      searchHint: 'Name, issuer, jurisdiction or number',
      idOf: (row) => row.RegistrationId,
      labelOf: (row) => row.Name,
      filters: [
        { param: 'status', label: 'Status', options: REGISTRATION_STATUS },
        { param: 'itemType', label: 'Type', options: REGISTRATION_TYPE },
      ],
      columns: [
        {
          key: 'Name',
          label: 'Item',
          render: (row) => (
            <div>
              <div className="admin-cell-title">
                {row.DocumentLink ? (
                  <a href={row.DocumentLink} target="_blank" rel="noreferrer noopener">
                    {row.Name}
                  </a>
                ) : (
                  row.Name
                )}
              </div>
              <div className="xs muted">
                {snakeLabel(row.ItemType)}
                {row.Issuer ? ` · ${row.Issuer}` : ''}
                {row.Jurisdiction ? ` · ${row.Jurisdiction}` : ''}
              </div>
            </div>
          ),
        },
        {
          key: 'Identifier',
          label: 'Number',
          render: (row) =>
            row.Identifier ? <span className="mono">{row.Identifier}</span> : <span className="sub">—</span>,
        },
        {
          key: 'Status',
          label: 'Status',
          render: (row) => (
            <div className="admin-due">
              <StatusChip band={registrationBand(row.Status)}>{snakeLabel(row.Status)}</StatusChip>
              {/* Inside its own lead time and not already being renewed — the
                  one flag that means "start this now". */}
              {row.ActionRequired && row.Status !== 'renewal_in_progress' && (
                <StatusChip band="serious">start now</StatusChip>
              )}
            </div>
          ),
        },
        {
          key: 'ExpiryDate',
          label: 'Expires',
          render: (row) => {
            if (row.Status === 'not_required') return <span className="sub">not required</span>;
            return (
              <div className="admin-due">
                <span>{formatIsoShort(row.ExpiryDate)}</span>
                <StatusChip band={urgency(row.DaysToExpiry)}>{relativeDays(row.DaysToExpiry)}</StatusChip>
              </div>
            );
          },
        },
        {
          key: 'RenewalLeadDays',
          label: 'Lead time',
          align: 'right',
          render: (row) => `${row.RenewalLeadDays} d`,
        },
        {
          key: 'CoverageAmount',
          label: 'Cover',
          align: 'right',
          render: (row) =>
            row.CoverageAmount === null ? (
              <span className="sub">—</span>
            ) : (
              formatCompactCurrency(row.CoverageAmount)
            ),
        },
        {
          key: 'PremiumAnnual',
          label: 'Premium',
          align: 'right',
          render: (row) =>
            row.PremiumAnnual === null ? (
              <span className="sub">—</span>
            ) : (
              formatCompactCurrency(row.PremiumAnnual)
            ),
        },
        {
          key: 'OwnerName',
          label: 'Owner',
          render: (row) => row.OwnerName ?? <span className="admin-warn-text">unassigned</span>,
        },
      ],
      fields: [
        { name: 'name', label: 'Name', kind: 'text', wide: true },
        { name: 'itemType', label: 'Type', kind: 'select', options: REGISTRATION_TYPE },
        { name: 'status', label: 'Status', kind: 'select', options: REGISTRATION_STATUS },
        { name: 'issuer', label: 'Issuer', kind: 'text' },
        { name: 'jurisdiction', label: 'Jurisdiction', kind: 'text' },
        {
          name: 'identifier',
          label: 'Policy / registration number',
          kind: 'text',
          wide: true,
        },
        { name: 'issueDate', label: 'Issued', kind: 'date' },
        {
          name: 'expiryDate',
          label: 'Expires',
          kind: 'date',
          hint: 'Required. An item with no expiry cannot be chased, and chasing is what this register is for.',
        },
        {
          name: 'renewalLeadDays',
          label: 'Renewal lead time (days)',
          kind: 'int',
          hint: 'How far ahead this one needs starting. A licence might be a week; a liability policy two months.',
        },
        { name: 'coverageAmount', label: 'Coverage amount', kind: 'money' },
        { name: 'premiumAnnual', label: 'Annual premium', kind: 'money' },
        {
          name: 'ownerEmployeeId',
          label: 'Owner',
          kind: 'select',
          optional: true,
          placeholder: 'Unassigned',
          choices: (employees.data ?? []).map((e) => ({
            value: String(e.EmployeeId),
            label: e.FullName,
          })),
        },
        { name: 'documentLink', label: 'Document link', kind: 'text', wide: true },
        { name: 'notes', label: 'Notes', kind: 'textarea', wide: true },
      ],
      blank: {
        name: '',
        itemType: 'registration',
        status: 'current',
        issuer: '',
        jurisdiction: '',
        identifier: '',
        issueDate: '',
        expiryDate: '',
        renewalLeadDays: 45,
        coverageAmount: '',
        premiumAnnual: '',
        ownerEmployeeId: '',
        documentLink: '',
        notes: '',
      },
      toForm: (row) => ({
        name: row.Name,
        itemType: row.ItemType,
        status: row.Status,
        issuer: row.Issuer ?? '',
        jurisdiction: row.Jurisdiction ?? '',
        identifier: row.Identifier ?? '',
        issueDate: row.IssueDate?.slice(0, 10) ?? '',
        expiryDate: row.ExpiryDate?.slice(0, 10) ?? '',
        renewalLeadDays: row.RenewalLeadDays,
        coverageAmount: row.CoverageAmount ?? '',
        premiumAnnual: row.PremiumAnnual ?? '',
        ownerEmployeeId: row.OwnerEmployeeId ?? '',
        documentLink: row.DocumentLink ?? '',
        notes: row.Notes,
      }),
    }),
    [employees.data],
  );

  const reload = () => {
    analytics.reload();
    onChanged();
  };

  const d = analytics.data;
  const s = d?.summary;

  return (
    <div className="admin-stack">
      {analytics.error && <ErrorBanner message={analytics.error} />}
      {!d && analytics.loading && <Loading label="Loading the register" />}

      {d && s && s.total + s.notRequired > 0 && (
        <>
          <div className="grid grid-4">
            <Stat
              label="Lapsed"
              value={s.lapsed}
              flagged={s.lapsed > 0}
              foot={`of ${num(s.total)} tracked`}
            />
            <Stat
              label="Needs starting"
              value={s.actionRequired}
              flagged={s.actionRequired > 0}
              /* Not the same as "expires in 30 days" — the window is per item. */
              foot="inside its own renewal lead time"
            />
            <Stat
              label="In date"
              value={s.inDatePercent === null ? '—' : pct(s.inDatePercent, 0)}
              foot={`${s.expiring30} expiring within 30 days`}
            />
            <Stat
              label="Annual premium"
              value={formatCompactCurrency(s.annualPremium)}
              foot={`${formatCompactCurrency(s.totalCoverage)} of cover`}
            />
          </div>

          {s.inRenewal > 0 && (
            <p className="sub">
              {num(s.inRenewal)} {s.inRenewal === 1 ? 'item is' : 'items are'} marked as renewal in
              progress.
              {s.notRequired > 0 &&
                ` ${num(s.notRequired)} more ${s.notRequired === 1 ? 'is' : 'are'} recorded as not required and counted in none of the figures above.`}
            </p>
          )}

          <ChartCard
            title="By type"
            subtitle="What the register holds, and what it costs to keep."
            table={{
              columns: [
                {
                  key: 'itemType',
                  label: 'Type',
                  render: (row: RegistrationsData['byType'][number]) => snakeLabel(row.itemType),
                },
                { key: 'count', label: 'Items', align: 'right' },
                {
                  key: 'premiumAnnual',
                  label: 'Annual premium',
                  align: 'right',
                  render: (row: RegistrationsData['byType'][number]) =>
                    formatCompactCurrency(row.premiumAnnual),
                },
                {
                  key: 'coverageAmount',
                  label: 'Cover',
                  align: 'right',
                  render: (row: RegistrationsData['byType'][number]) =>
                    formatCompactCurrency(row.coverageAmount),
                },
              ],
              rows: d.byType,
              rowKey: (row) => row.itemType,
            }}
          >
            <RankedBars
              rows={d.byType.map((t) => ({
                label: snakeLabel(t.itemType),
                value: t.count,
                meta:
                  t.premiumAnnual > 0
                    ? `${formatCompactCurrency(t.premiumAnnual)} a year`
                    : 'no premium recorded',
              }))}
              format={(v) => num(v)}
              labelWidth={132}
            />
          </ChartCard>

          <Card
            title="The renewal calendar"
            subtitle="From three months overdue out to a year ahead, soonest first. Items marked not required are left out."
          >
            <DataTable
              columns={TIMELINE_COLUMNS}
              rows={d.timeline}
              rowKey={(row) => String(row.registrationId)}
              empty="Nothing expires in the next year."
            />
          </Card>
        </>
      )}

      <RegisterPanel spec={spec} canWrite={canWrite} onChanged={reload} />
    </div>
  );
}

const TIMELINE_COLUMNS: Column<RegistrationsData['timeline'][number]>[] = [
  {
    key: 'name',
    label: 'Item',
    render: (row) => (
      <div>
        <div className="admin-cell-title">{row.name}</div>
        <div className="xs muted">
          {snakeLabel(row.itemType)}
          {row.issuer ? ` · ${row.issuer}` : ''}
          {row.jurisdiction ? ` · ${row.jurisdiction}` : ''}
        </div>
      </div>
    ),
  },
  {
    key: 'identifier',
    label: 'Number',
    render: (row) => (row.identifier ? <span className="mono">{row.identifier}</span> : <span className="sub">—</span>),
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => (
      <div className="admin-due">
        <StatusChip band={registrationBand(row.status)}>{snakeLabel(row.status)}</StatusChip>
        {row.actionRequired && row.status !== 'renewal_in_progress' && (
          <StatusChip band="serious">start now</StatusChip>
        )}
      </div>
    ),
  },
  {
    key: 'expiryDate',
    label: 'Expires',
    render: (row) => (
      <div className="admin-due">
        <span>{formatIsoShort(row.expiryDate)}</span>
        <StatusChip band={urgency(row.daysToExpiry)}>{relativeDays(row.daysToExpiry)}</StatusChip>
      </div>
    ),
  },
  {
    key: 'renewalLeadDays',
    label: 'Lead time',
    align: 'right',
    render: (row) => `${row.renewalLeadDays} d`,
  },
  {
    key: 'premiumAnnual',
    label: 'Premium',
    align: 'right',
    render: (row) => (row.premiumAnnual === null ? '—' : formatCompactCurrency(row.premiumAnnual)),
  },
  {
    key: 'ownerName',
    label: 'Owner',
    render: (row) => row.ownerName ?? <span className="admin-warn-text">unassigned</span>,
  },
];
